import { DropDown, Button, Icon, Modal, useDevice } from '@tecma/ds';
import { useTranslation } from '@tecma/i18n';
import { useEffect, useState } from 'react';

import { DELETABLE_QUOTE_STATUSES } from 'constants/quoteStatus';
import { QuoteStatus } from 'types/quote';

import './quote-details-actions.scss';

interface Props {
  downloadDocuments: () => void;
  handleOpenPdf: () => void;
  handleDeleteQuote: () => void;
  quoteName: string;
  quoteStatus: QuoteStatus;
}

export const QuoteDetailsActions: React.FC<Props> = ({ downloadDocuments, handleOpenPdf, handleDeleteQuote, quoteName, quoteStatus }) => {
  const { t } = useTranslation();
  const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
  const [isActionsModalOpen, setIsActionsModalOpen] = useState(false);
  const [isDeleteQuoteModalOpen, setIsDeleteQuoteModalOpen] = useState(false);
  const device = useDevice();

  const toggleActionsDisplay = () => {
    if (device.type === 'desktop') {
      setIsActionsDropdownOpen((prev) => !prev);
      setIsActionsModalOpen(false);
    } else {
      setIsActionsModalOpen((prev) => !prev);
      setIsActionsDropdownOpen(false);
    }
  };

  const toggleDeleteQuoteModal = () => {
    setIsDeleteQuoteModalOpen((prev) => !prev);
    if (isActionsDropdownOpen || isActionsModalOpen) {
      toggleActionsDisplay();
    }
  };

  const onDeleteQuote = () => {
    toggleDeleteQuoteModal();
    handleDeleteQuote();
  };

  const isDeletableQuote = DELETABLE_QUOTE_STATUSES.includes(quoteStatus);

  useEffect(() => {
    if ((isActionsDropdownOpen && device.type !== 'desktop') || (isActionsModalOpen && device.type === 'desktop')) {
      toggleActionsDisplay();
    }
  }, [device.type]);

  return (
    <>
      <Modal
        className='quote-details-actions-delete-quote-modal'
        isOpen={isDeleteQuoteModalOpen}
        onClose={toggleDeleteQuoteModal}
        closeOnBackDropClick
      >
        <Modal.Header onClose={toggleDeleteQuoteModal} title={t('delete-quote.title')} closeIcon />
        <Modal.Content>
          <p>{t('delete-quote.description')}</p>
        </Modal.Content>
        <Modal.Footer>
          <Button onClick={toggleDeleteQuoteModal} outlined>
            {t('actions.close')}
          </Button>
          <Button onClick={onDeleteQuote} color='danger'>
            {t('actions.delete')}
          </Button>
        </Modal.Footer>
      </Modal>
      <div className='quote-details-actions'>
        <Button onClick={downloadDocuments} iconName='folder-download' outlined>
          <span>{t('actions.download-documents')}</span>
        </Button>
        <DropDown
          id='quote-details-actions-dropdown'
          isOpen={isActionsDropdownOpen}
          onToggle={toggleActionsDisplay}
          triggerProps={{ iconName: 'dots-vertical', outlined: true, onClick: toggleActionsDisplay }}
          position={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <DropDown.Item onClick={handleOpenPdf} className='quote-details-actions-open-pdf'>
            <Icon iconName='external-link' size='small' />
            <span>{t('actions.open-pdf')}</span>
          </DropDown.Item>
          <DropDown.Item
            onClick={isDeletableQuote ? toggleDeleteQuoteModal : () => {}}
            className={`quote-details-actions-delete-quote ${!isDeletableQuote ? 'disabled' : ''}`}
          >
            <Icon iconName='trash' size='small' />
            <div className='delete-quote-button-content'>
              <span>{t('actions.delete')}</span>
              {!isDeletableQuote && <span className='delete-quote-button-description'>{t('actions.cant-delete-description')}</span>}
            </div>
          </DropDown.Item>
        </DropDown>

        <Modal className='quote-details-actions-modal' isOpen={isActionsModalOpen} onClose={toggleActionsDisplay} closeOnBackDropClick>
          <Modal.Header onClose={toggleActionsDisplay} title={quoteName} closeIcon />
          <Modal.Content>
            <Button
              onClick={toggleActionsDisplay}
              className='quote-details-actions-modal-open-pdf'
              iconName='external-link'
              color='transparent'
              fluid
            >
              {t('actions.open-pdf')}
            </Button>
            <Button
              onClick={handleDeleteQuote}
              className='quote-details-actions-modal-delete-quote'
              iconName='trash'
              color='transparent'
              disabled={!isDeletableQuote}
              fluid
            >
              <div className='delete-quote-button-content'>
                {t('actions.delete')}
                {!isDeletableQuote && <span className='delete-quote-button-description'>{t('actions.cant-delete-description')}</span>}
              </div>
            </Button>
          </Modal.Content>
        </Modal>
      </div>
    </>
  );
};
